import type { CountryCode } from '@/data';

export type QuestionId =
  | 'horizon'
  | 'passport'
  | 'visa_pathway'
  | 'climate'
  | 'urban_density'
  | 'budget'
  | 'income'
  | 'income_amount'
  | 'tax_residency_intent'
  | 'priorities'
  | 'family'
  | 'family_children_count'
  | 'languages'
  | 'dealbreakers'
  | 'ties'
  | 'buy_property'
  | 'career_intent';

// Career-intent gate — the single, profession-agnostic work signal in the quiz.
// Drives whether the optional work-&-income layer activates (next-steps "Work &
// income" phase, jobs dimension surfacing, work consultation CTA). Currently
// captured but does NOT change scoring; the generic jobs overlay arrives behind
// SCORING_V3_CAREER (CV.0.1 §7 / CV.4). The four IT-framed follow-up types that
// used to live here were removed in CV.0.1 — work depth is captured later, in
// next-steps, not in the quiz. See CV.0.1-conscious-relocation-repositioning.md.
export type CareerIntent = 'primary' | 'eventual' | 'none';

// Visa pathway groups — used both as quiz multi-select answers AND as
// indices for matching against country.metrics.visa.*_path fields.
// `unknown_exploring` is a soft signal that the user hasn't decided yet —
// scoring stays neutral instead of penalising.
export type VisaPathway =
  | 'digital_nomad'
  | 'freelance_self_employed'
  | 'golden_investor'
  | 'family_reunion'
  | 'ancestry'
  | 'work_sponsor'
  | 'student_to_perm'
  | 'refugee_protection'
  | 'unknown_exploring';

// Stance the user takes on tax residency in their origin country. Drives
// the tax_residency_multiplier in scoring once country.tax.territorial /
// .dtt_with data lands.
export type TaxResidencyIntent =
  | 'break_origin'
  | 'keep_origin'
  | 'dual'
  | 'treaty_minimize'
  | 'unsure';

// Income bucket — separate from `income` (which captures TYPE: remote /
// local / business / passive). Needed for visa-pathway eligibility
// (most DN visas require ~€2.5–3k/mo proof of income) and to refine cost
// scoring against the user's actual cash-flow.
export type IncomeAmountBucket = 'lt1500' | '1500to3000' | '3000to6000' | 'gt6000';

// Number-of-children follow-up, gated behind `family` having a kids-* option
// selected (see schema.ts `family_children_count.visible_when`). Needed to
// resolve the household bucket for cost_of_living.json — the `family`
// question alone can't distinguish 1 child from 3, but the cost data carries
// separate baskets per count (see derive.ts `deriveHouseholdBucket`).
export type ChildrenCount = '1' | '2' | '3plus';

// Passport groups — drive banking, Schengen visa, and de-facto-treatment
// logic. The three post-Soviet passports are tracked separately because
// destination countries treat them very differently (Stage 4.7, 2026-05-24):
// RU often `hostile`/`restrictive`, UA often `welcoming` via Temporary
// Protection, BY usually parallel to RU but a notch less severe.
//
// 'eu'    — any EU member state passport (free movement inside EU/Schengen).
// 'anglo' — US / UK / AU / CA / NZ (broadly visa-friendly worldwide).
// 'ru'    — Russian passport (heavy sanctions / banking friction / property freezes in several EU countries).
// 'by'    — Belarus passport (parallel sanctions regime; typically RU-equivalent in banking, slightly less severe).
// 'ua'    — Ukrainian passport (EU Temporary Protection through 2026; near-welcoming everywhere in EU/EEA).
// 'other' — everything else (Asia, LATAM, Africa, Middle East).
export type Passport = 'eu' | 'anglo' | 'ru' | 'by' | 'ua' | 'other';

// Legacy value kept only as a transition signal. Persisted answers from
// before Stage 4.7 may carry 'cis' — we auto-map to 'ru' (most conservative
// of the three) on load and prompt the user to clarify.
export type LegacyCisPassport = 'cis';

export type QuestionType = 'single' | 'multi' | 'budget-slider';

export type Dimension =
  | 'visa'
  | 'tax'
  | 'cost'
  | 'climate'
  | 'healthcare'
  | 'safety'
  | 'language'
  | 'jobs'
  | 'family'
  | 'banking';

export interface QuestionOption {
  id: string;
  label_key: string;
  // Short sentence under the option explaining what choosing it means
  // for the user. Always-visible on mobile, hover/focus on desktop.
  // Standard from CV.0 §4.4 — required for new questions, optional for
  // legacy ones.
  help_key?: string;
  // Implicit weights — boost applied to scoring dimensions when this option is picked.
  weights?: Partial<Record<Dimension, number>>;
}

export interface Question {
  id: QuestionId;
  type: QuestionType;
  label_key: string;
  description_key?: string;
  options?: QuestionOption[];
  skippable?: boolean;
  multi_max?: number;
  // Conditional visibility — when defined and returns false against the
  // current answer set, the question is silently skipped in the quiz flow.
  // Hidden questions do NOT have their answers cleared (re-entering the
  // gate state restores them).
  visible_when?: (answers: Answers) => boolean;
}

export type BudgetBucket = 1 | 2 | 3 | 4;

export interface Answer {
  questionId: QuestionId;
  optionIds: string[];
  budgetBucket?: BudgetBucket;
}

export type Answers = Partial<Record<QuestionId, Answer>>;

export type ScoringWeights = Record<Dimension, number>;

export interface CountryMatchResult {
  countryCode: CountryCode;
  // 0..1 (UI multiplies by 100 for percent display).
  score: number;
  excluded: boolean;
  exclusionReasons?: string[];
  components: Partial<Record<Dimension, number>>;
  penalties: { total: number; reasons: string[] };
  // Up to 1 short reason for the result card.
  blockers: string[];
}

export interface QuizResult {
  topMatches: CountryMatchResult[];
  excluded: CountryMatchResult[];
  // Countries without metrics — placeholders for future tiers.
  unscored: CountryCode[];
  weights: ScoringWeights;
  generatedAt: string;
  schemaVersion: number;
}

// Helper: extract the passport group from quiz answers (undefined if unanswered).
// Backwards-compat: legacy 'cis' answers are mapped to 'ru' (the most
// conservative of the three). The UI separately surfaces a "is this still
// correct?" prompt so we don't silently misclassify Belarus/Ukraine users.
export function getPassport(answers: Answers): Passport | undefined {
  const id = answers.passport?.optionIds[0];
  if (id === 'eu' || id === 'anglo' || id === 'ru' || id === 'by' || id === 'ua' || id === 'other') {
    return id;
  }
  if (id === 'cis') return 'ru';
  return undefined;
}

// Did the user persist a pre-Stage-4.7 'cis' value? We only auto-map on
// read; this exposes the raw signal so the UI can offer a re-answer prompt.
export function isLegacyCisPassport(answers: Answers): boolean {
  return answers.passport?.optionIds[0] === 'cis';
}
